import { Injectable } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { LimitDialogComponent } from './components/limit-dialog/limit-dialog.component';
import {
  ConfirmDialogComponent,
  ConfirmDialogData,
} from './components/confirm-dialog/confirm-dialog.component';
import {
  ClipboardDialogComponent,
  ClipboardDialogData,
} from './components/clipboard-dialog/clipboard-dialog.component';
import { DialogIds } from './models/dialog-ids';
import { EngineService } from '../engine/engine.service';
import { EventsEnum } from '../engine/models/events.enum';
import { environment } from 'src/environments/environment';
import {
  IframeDialogComponent,
  IframeDialogData,
} from './components/iframe-dialog/iframe-dialog.component';
import { Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { capitalize, get, isNil } from 'lodash';
import { Store } from '@ngxs/store';
import { AuthState } from '../auth/state/auth/auth.state';
import { OrganizationsState } from '../organizations/state/organizations.state';
import { PendoService } from '../shared/pendo.service';
import { ContentViewService } from '../shared/content-views.service';

const mobileScreenWidth = 959;
interface PricingMessage {
  plan:
    | 'personal'
    | 'personal-plus'
    | 'class'
    | 'institution'
    | 'starter'
    | 'premium'
    | 'enterprise';
  action: 'try' | 'contact' | 'buy' | 'sign-up';
}

const fullScreenConfig = {
  maxWidth: '100vw',
  width: `100vw`,
  height: `100vh`,
  panelClass: 'full-modal',
};
@Injectable({
  providedIn: 'root',
})
export class DialogService {
  get isMobile(): boolean {
    return window.innerWidth <= mobileScreenWidth;
  }

  constructor(
    private dialog: MatDialog,
    private engineService: EngineService,
    private router: Router,
    private store: Store,
    private pendoService: PendoService,
    private contentViewsService: ContentViewService
  ) {}

  openClipboardDialog(
    copyText: string,
    config?: MatDialogConfig<ClipboardDialogData>
  ) {
    const options = {
      data: {
        id: DialogIds.ClipboardDialog,
        copyText: copyText,
      },
      ...config,
    };

    return this.dialog.open(ClipboardDialogComponent, options);
  }

  openConfirmDialog(config?: MatDialogConfig<ConfirmDialogData>) {
    const options = {
      id: DialogIds.ConfirmDialog,
      ...config,
    };

    return this.dialog.open(ConfirmDialogComponent, options);
  }

  openLimitDialog() {
    this.closeAllDialogs();

    return this.dialog.open(LimitDialogComponent, {
      id: DialogIds.LimitDialog,
      width: '480px',
    });
  }

  openUpgradeDialog(closeAll: boolean = true) {
    if (closeAll) {
      this.closeAllDialogs();
    }

    // If web, use the traditional upgrade modal

    // Determine which plan the user currently has.
    // Note that there's currently no way know what business plan they have, so that defaults to null but
    // we presume the business tab

    // Uses https://pricing.biodigital.com
    // Repo: https://github.com/biodigital-human/human-pricing
    const profile = this.store.selectSnapshot(AuthState.getUserProfile);

    // Retrieve accountType and subscription to pass to pricing.biodigital.com
    const accountType = profile.premium_account_valid ? profile.premium_account_type : 'free';
    const subscription = encodeURIComponent(get(profile, 'organizations[0].subscription.type', '')).toLowerCase();
    const isTrialExpired = this.store.selectSnapshot(OrganizationsState.isTrialExpired);

    // Determine starting pricing page based on user segmentation account purpose
    const accountSetting = profile.account_settings.find(
      (setting) => 'account_purpose' in setting
    );
    
    let page: string;
    const accountPurpose = get(accountSetting, 'account_purpose.value', '').toLowerCase();
    switch (accountPurpose) {
      case 'business':
        page = 'business.html';
        break;
      case 'education':
        page = 'school.html';
        break;
      default:
        page = 'individual.html';
        break;
    }


    const pricingPage = `${page}?accountType=${accountType}&subscription=${subscription}&expired=${isTrialExpired}`;
    const pricingUrl = environment.native ?
      pricingPage :
      `https://pricing.biodigital.com/${pricingPage}`;
      // `https://localhost:1234/${pricingPage}`; // Development only

    // Initialize pricing options for the pricing modal and iframe
    const pricingOptions = {
      id: DialogIds.PricingDialog,
      ...fullScreenConfig,
      data: <IframeDialogData>{
        url: pricingUrl,
        backgroundColor: '#0B1220', // Match pricing page BG
      },
    };

    // Determine returnURL so the user has a way out of upgrading
    const returnUrl = encodeURIComponent(this.router.url);

    // Pop open the pricing iframe dialog and respond to any message it returns
    return this.dialog
      .open(IframeDialogComponent, pricingOptions)
      .afterClosed()
      .subscribe((data: PricingMessage) => {
        if (data) {
          const plans = {
            business: ['starter', 'premium', 'enterprise'],
            education: ['class', 'institution']
          };
  
          // Track pendo event - include user action and plan type
          let planPage = 'Individual';
          Object.entries(plans).forEach(([group, values]) => {
            if (values.indexOf(data.plan)) {
              planPage = group;
            }
          });
  
          if (data.action) {
            this.pendoService.track(`Pricing - ${capitalize(data.action)} - ${capitalize(data.plan)}`, {
              platform: this.contentViewsService.getProductType(),
              page: planPage
            });
          }
  
          switch (data.action) {
            case 'buy':
              // If native, bypass the iframe upgrade path and let the app handle it
              if (environment.native) {
                this.engineService.fireEvent(EventsEnum.NativeUpgrade, null);
                return;
              }
  
              this.router.navigate(['/account/profile'], {
                queryParams: {
                  section: 'subscription',
                  mode: 'edit',
                },
              });
              break;
  
            case 'contact':
              this.openContactDialog();
              break;
  
            case 'try':
              let workflow = '';
              switch (true) {
                case plans.business.includes(data.plan):
                  workflow =
                    `/account/workflow/team/create?purpose=Business&returnTo=${returnUrl}`;
                  break;
  
                case plans.education.includes(data.plan):
                  workflow =
                    `/account/workflow/team/create?purpose=Education&returnTo=${returnUrl}`;
                  break;
  
                default:
                  workflow = 'account/workflow';
              }
  
              this.router.navigateByUrl(workflow);
              break;
  
            default:
            // Do nothing
              break;
          }
        } else {
          this.pendoService.track('Pricing - Exit');
        }
      });
  }

  openContactDialog() {
    // get current organization: optional
    const organization = this.store.selectSnapshot(OrganizationsState.getOrganization);
    const organizationId = !isNil(organization) ? `${organization.id}` : '';

    if (environment.native) { // native: contact us
      const url = `https://www.biodigital.com/contact?organizationId=${organizationId}&utm_source=human-mobile`;

      return window.open(url, '_blank');
    } else { // web: contact us
      const url = `https://www.biodigital.com/headless/contact-us?organizationId=${organizationId}&utm_source=human-web`;

      const contactOptions = {
        id: DialogIds.ContactDialog,
        ...fullScreenConfig,
        data: <IframeDialogData>{
          url: url,
        },
      };

      return this.dialog.open(IframeDialogComponent, contactOptions);
    }
  }

  openPremiumContentDialog() {
    const upgradeEnabled = environment.native || !this.isMobile;

    const bodyText = upgradeEnabled
      ? 'This is premium content. Upgrade to a Premium Plan for unlimited access.'
      : 'This is premium content. Visit us on desktop to upgrade to a premium plan.';

    return this.openConfirmDialog({
      id: DialogIds.PremiumContentDialog,
      data: {
        titleText: 'Premium Content',
        bodyText: bodyText,
        confirmText: 'Upgrade Now',
        confirmEnabled: upgradeEnabled,
        cancelText: 'OK',
        confirmColor: 'warn',
      },
    });
  }

  closeAllDialogs() {
    this.dialog.closeAll();
  }
}
